import React, { Fragment, useCallback } from 'react';
import MaterialTable, {
  MTableBodyRow,
  MTableToolbar,
  MTableActions,
  MTableHeader,
} from 'material-table';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { Collapse } from 'reactstrap';
import { mdiNeedle } from '@mdi/js';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Refresh';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import PrintIcon from '@material-ui/icons/Print';
import SendIcon from '@material-ui/icons/Send';
import AssessmentIcon from '@material-ui/icons/Assessment';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import VisibilityIcon from '@material-ui/icons/Visibility';
import HistoryIcon from '@material-ui/icons/History';
import CheckIcon from '@material-ui/icons/Check';
import FilterListIcon from '@material-ui/icons/FilterList';
import ClearIcon from '@material-ui/icons/Clear';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import AssignmentIcon from '@material-ui/icons/Assignment';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import AppsIcon from '@material-ui/icons/Apps';
import { Tooltip, IconButton, SvgIcon, withStyles } from '@material-ui/core';
import { v4 as uuid } from 'uuid';
import Icon from '@mdi/react';
import { exportXLSX } from '../utils/smartutils';
import materialTableIcons from '../utils/materialTableIcons';
import { ACTION_NAME, COMMUNICATION_TYPE } from '../utils/constants';

function materialTable(props) {
  const {
    data,
    columns,
    title,
    t,
    pageSize,
    onRefreshClick,
    onAddClick,
    onEditClick,
    onRemoveClick,
    onPrintClick,
    onHistoryViewClick,
    onCardClick,
    paging,
    search,
    showTitle,
    selection,
    toolbar,
    onSentClick,
    actionsIndex,
    filterBuildFunction,
    filterToggle,
    isFiltering,
    onApproveClick,
    onReproveClick,
    onSelectionChange,
    onJsonExport,
    onViewTicketClick,
    minBodyHeight,
    maxBodyHeight,
    onApplyClick,
    actionsPosition,
    onViewMetricsClick,
    onViewVisualizationMetricsClick,
    onActivityCorrectionClick,
    onPrintReceiptClick,
    onPrintOneReceiptClick,
    memoType,
    actionsDisabledByConditional,
    isLoading,
    rowStyle,
    onCorrectionStudentActivityClick,
    group,
    maxBodyHeightAuto,
    onMedicationViewRecipe,
    onApplyMedicationClick,
    onCancelApplyMedicationClick,
    onAppClick,
    onViewVisualizationClick,
    customAction,
    customTableHeader,
    fixedFirstColumn,
    cellStyle,
    customActions,
  } = props;

  const theme = createTheme({
    palette: {
      primary: {
        main: '#9d5fbe',
      },
      secondary: {
        main: '#9d5fbe',
      },
    },
    overrides: {
      MuiToolbar: {
        gutters: {
          paddingLeft: '0px !important',
        },
      },
      MuiTableCell: {
        root: fixedFirstColumn
          ? {
              textAlign: 'center !important',
              '&:nth-child(1)': {
                background: '#FFF',
                position: 'sticky',
                left: 0,
              },
            }
          : {},
        head: {
          color: '#fff',
          background: '#986CAC !important',
        },
      },
      MuiTableSortLabel: {
        root: {
          color: '#fff',
          '&:hover': {
            color: '#debeed',
          },
        },
        active: {
          color: '#986CAC !important',
        },
        icon: {
          color: '#986CAC !important',
        },
      },
      MuiSvgIcon: {
        root: {
          color: '#6a2b8b',
        },
      },
      MuiTypography: {
        root: {
          color: '#6a2b8b',
          paddingLeft: '0px !important',
        },
        h6: {
          color: '#6a2b8b !important',
          fontSize: '14px',
        },
      },
      MuiInputBase: {
        input: {
          color: '#6a2b8b',
        },
      },
      MuiSelect: {
        icon: {
          color: '#6a2b8b',
        },
      },
    },
  });

  const MoreTooltip = withStyles(() => ({
    tooltip: {
      backgroundColor: '#fff',
      border: '1px solid rgba(0,0,0,0.06)',
    },
    arrow: {
      color: '#fff',
      '&::before': {
        backgroundColor: '#fff',
        border: '1px solid rgba(0,0,0,0.06)',
      },
    },
  }))(Tooltip);

  const actions = [];

  if (onRemoveClick) {
    actions.push({
      name: ACTION_NAME.REMOVE,
      icon: () => <DeleteIcon />,
      position: actionsPosition,
      tooltip: t('general.tables.delete_registry'),
      onClick: (event, rowData) => {
        onRemoveClick(event, rowData);
      },
    });
  }
  if (onRefreshClick) {
    actions.push({
      name: ACTION_NAME.REFRESH,
      isFreeAction: true,
      icon: () => <RefreshIcon />,
      tooltip: t('general.tables.refresh'),
      onClick: () => {
        onRefreshClick();
      },
    });
  }
  if (onAddClick) {
    actions.push({
      name: ACTION_NAME.ADD,
      isFreeAction: true,
      icon: () => <AddIcon />,
      tooltip: t('general.tables.add_registry'),
      onClick: (event, rowData) => {
        onAddClick(event, rowData);
      },
    });
  }

  if (onAppClick) {
    actions.push({
      name: ACTION_NAME.VIEW_GRID,
      isFreeAction: true,
      icon: () => <AppsIcon />,
      tooltip: onAppClick.tooltip,
      onClick: (event) => {
        onAppClick.callback(event);
      },
    });
  }

  if (onEditClick) {
    actions.push({
      name: ACTION_NAME.EDIT,
      icon: () => <EditIcon />,
      tooltip: t('general.tables.change_registry'),
      position: 'row',
      onClick: (event, rowData) => {
        onEditClick(event, rowData);
      },
    });
  }

  if (onCardClick) {
    actions.push({
      name: ACTION_NAME.CARD,
      icon: () => <AssignmentIcon />,
      tooltip: t('screens.mnt_alunos.printCard'),
      position: 'row',
      onClick: (event, rowData) => {
        onCardClick(rowData);
      },
    });
  }

  if (onPrintClick) {
    actions.push({
      name: ACTION_NAME.PRINT,
      icon: () => <PrintIcon />,
      tooltip: t('general.tables.print'),
      position: 'row',
      onClick: (event, rowData) => {
        onPrintClick(event, rowData);
      },
    });
  }
  if (onPrintReceiptClick) {
    actions.push({
      name: ACTION_NAME.PRINT_RECEIPT,
      icon: () => <PrintIcon />,
      tooltip: t('general.tables.print'),
      position: actionsPosition,
      onClick: (event, rowData) => {
        onPrintReceiptClick(event, rowData);
      },
    });
  }

  if (onSentClick) {
    actions.push({
      name: ACTION_NAME.SENT,
      icon: () => <SendIcon />,
      tooltip: t('general.tables.send'),
      onClick: (event, rowData) => {
        onSentClick(event, rowData);
      },
    });
  }

  if (onApproveClick) {
    actions.push({
      name: ACTION_NAME.APPROVE,
      icon: () => <CheckIcon />,
      tooltip: t('general.tables.approve'),
      onClick: (event, rowData) => {
        onApproveClick(event, rowData);
      },
    });
  }

  if (onHistoryViewClick) {
    actions.push({
      name: ACTION_NAME.HISTORY_VIEW,
      icon: () => <HistoryIcon />,
      tooltip: t('general.tables.history'),
      onClick: (event, rowData) => {
        onHistoryViewClick(event, rowData);
      },
    });
  }

  if (onReproveClick) {
    actions.push({
      name: ACTION_NAME.REPROVE,
      icon: () => <ClearIcon />,
      tooltip: t('general.tables.reprove'),
      onClick: (event, rowData) => {
        onReproveClick(event, rowData);
      },
    });
  }
  if (onApplyClick) {
    actions.push({
      name: ACTION_NAME.APPLY,
      icon: () => {
        return (
          <Icon
            path={mdiNeedle}
            horizontal
            vertical
            size={1}
            rotate={180}
            color="#00008A"
            id="mdiNeedle"
          />
        );
      },
      tooltip: t('vaccination.apply'),
      onClick: (event, rowData) => {
        onApplyClick(rowData);
      },
    });
  }
  if (onPrintOneReceiptClick) {
    actions.push({
      name: ACTION_NAME.PRINT_ONE_RECEIPT,
      icon: () => <PrintIcon />,
      tooltip: t('report.parameters.print-receipt'),
      position: 'row',
      onClick: (event, rowData) => {
        onPrintOneReceiptClick(event, rowData);
      },
    });
  }

  if (filterBuildFunction) {
    actions.push({
      name: ACTION_NAME.SHOW_FILTER,
      isFreeAction: true,
      icon: () => <FilterListIcon />,
      tooltip: t('general.tables.filter-list'),
      onClick: () => filterToggle(),
    });
  }

  if (onJsonExport) {
    actions.push({
      name: ACTION_NAME.JSON_EXPORT,
      icon: () => <CloudDownloadIcon />,
      tooltip: t('general.tables.download-date'),
      onClick: (event, rowData) => {
        onJsonExport(rowData);
      },
    });
  }

  if (onViewMetricsClick) {
    actions.push({
      name: ACTION_NAME.VIEW_METRICS,
      icon: () => <AssessmentIcon />,
      tooltip: t('general.tables.view-metrics'),
      onClick: (event, rowData) => {
        onViewMetricsClick(event, rowData);
      },
    });
  }

  if (onViewVisualizationMetricsClick) {
    actions.push({
      name: ACTION_NAME.VIEW_VISUALIZATION_METRICS,
      icon: () => <VisibilityIcon />,
      tooltip: t('general.tables.view-visualization-metrics'),
      onClick: (event, rowData) => {
        onViewVisualizationMetricsClick(event, rowData);
      },
    });
  }

  if (
    (onActivityCorrectionClick && memoType === COMMUNICATION_TYPE.ACTIVITY) ||
    memoType === COMMUNICATION_TYPE.EXAM
  ) {
    const tooltip =
      memoType === COMMUNICATION_TYPE.ACTIVITY
        ? t('communication.type-activity-correction')
        : t('communication.type-evaluation-correction');
    actions.push({
      name: ACTION_NAME.ACTIVITY_CORRECTION,
      icon: () => <AssignmentTurnedInIcon />,
      tooltip,
      onClick: (event, rowData) => {
        onActivityCorrectionClick(event, rowData);
      },
    });
  }

  if (onViewTicketClick) {
    actions.push({
      name: ACTION_NAME.VIEW_TICKET,
      icon: () => <FileCopyIcon />,
      tooltip: t('general.print-ticket'),
      position: 'row',
      onClick: (event, rowData) => onViewTicketClick(event, rowData),
    });
  }

  if (onCorrectionStudentActivityClick) {
    actions.push({
      name: ACTION_NAME.CORRECTION_STUDENT_ACTIVITY,
      icon: () => <AssignmentTurnedInIcon />,
      tooltip: t('communication.type-activity-correction'),
      onClick: (event, rowData) => {
        onCorrectionStudentActivityClick(event, rowData);
      },
    });
  }

  if (onApplyMedicationClick) {
    actions.push({
      icon: () => <CheckIcon />,
      tooltip: t('medication_application.apply'),
      onClick: (event, rowData) => {
        const ApliRowData = rowData.map((item) => ({
          id: item.id,
          applicationStatus: 'APLI',
          observation: item.observation,
          studentId: item.medication.student ? item.medication.student.id : 0,
        }));
        onApplyMedicationClick(event, ApliRowData);
      },
    });
  }

  if (onCancelApplyMedicationClick) {
    actions.push({
      icon: () => <ClearIcon />,
      tooltip: t('general.cancel'),
      onClick: (event, rowData) => {
        onCancelApplyMedicationClick(event, rowData);
      },
    });
  }

  if (onMedicationViewRecipe) {
    actions.push({
      name: ACTION_NAME.VIEW_MEDICATION_RECIPE,
      icon: () => <AssignmentIcon />,
      position: 'row',
      tooltip: t('mnt_medication.view-recipe'),
      onClick: (event, rowData) => {
        onMedicationViewRecipe(event, rowData);
      },
    });
  }

  if (actions.length > 5) {
    actions.push({
      name: ACTION_NAME.MORE,
      icon: () => <MoreVertIcon />,
      tooltip: t('general.more'),
      position: 'row',
    });
  }

  let maxBodyHeightValue = window.innerHeight - 190;
  if (maxBodyHeight) {
    maxBodyHeightValue = window.innerHeight - maxBodyHeight;
  } else if (maxBodyHeightAuto) {
    maxBodyHeightValue = 'auto';
  }

  if (onViewVisualizationClick) {
    actions.push({
      name: ACTION_NAME.VIEW_VISUALIZATION,
      icon: () => <VisibilityIcon />,
      tooltip: onViewVisualizationClick.tooltip,
      onClick: (event, rowData) => {
        onViewVisualizationClick.callback(event, rowData);
      },
    });
  }

  if (customAction) {
    actions.push({
      name: customAction.name,
      icon: () => customAction.icon,
      tooltip: customAction.tooltip,
      onClick: (event, rowData) => {
        customAction.callback(event, rowData);
      },
    });
  }

  if (customActions.length) {
    customActions.forEach((actionToolbar) => {
      actions.push(actionToolbar);
    });
  }

  return (
    <MuiThemeProvider theme={theme}>
      <MaterialTable
        onSelectionChange={onSelectionChange}
        columns={columns}
        data={data}
        title={title}
        actions={actions}
        icons={materialTableIcons}
        isLoading={isLoading}
        components={{
          Toolbar: (propss) => (
            <Fragment>
              <MTableToolbar {...propss} />
              <Collapse isOpen={isFiltering} appear>
                {filterBuildFunction ? filterBuildFunction() : null}
              </Collapse>
            </Fragment>
          ),
          Header: (propss) => {
            if (customTableHeader) {
              return customTableHeader({ ...propss });
            }
            return <MTableHeader {...propss} />;
          },
          Row: useCallback((propss) => {
            if (actionsDisabledByConditional) {
              actionsDisabledByConditional(propss);
            }
            return <MTableBodyRow {...propss} />;
          }, []),
          Actions: useCallback((propss) => {
            const copyProps = { ...propss };
            const findMore = propss.actions.find(
              (act) => act.name === ACTION_NAME.MORE
            );
            if (propss && propss.actions.length && findMore) {
              copyProps.actions.find(
                (a) => a.name === ACTION_NAME.MORE
              ).hidden = true;
              const actionsForTooltip = copyProps.actions
                .filter(
                  (act) =>
                    act.name !== ACTION_NAME.EDIT &&
                    act.name !== ACTION_NAME.REMOVE &&
                    act.name !== ACTION_NAME.MORE
                )
                .filter((act) => !act.disabled);
              actionsForTooltip.forEach((act) => {
                copyProps.actions.find(
                  (a) => a.name === act.name
                ).hidden = true;
              });
              return (
                <Fragment>
                  <MTableActions {...propss} />
                  {actionsForTooltip.length > 1 ? (
                    <MoreTooltip
                      interactive
                      arrow
                      placement="bottom-end"
                      title={
                        <>
                          {actionsForTooltip.map((act) => (
                            <Tooltip key={uuid()} title={act.tooltip}>
                              <IconButton
                                onClick={(evt) => act.onClick(evt, propss.data)}
                              >
                                <SvgIcon>
                                  <path
                                    d={
                                      act.icon().type.type.render().props
                                        .children.props.d
                                    }
                                  />
                                </SvgIcon>
                              </IconButton>
                            </Tooltip>
                          ))}
                        </>
                      }
                    >
                      <IconButton>
                        <SvgIcon>
                          <path
                            d={
                              findMore.icon().type.type.render().props.children
                                .props.d
                            }
                          />
                        </SvgIcon>
                      </IconButton>
                    </MoreTooltip>
                  ) : (
                    <>
                      {actionsForTooltip.map((act) => (
                        <Tooltip key={uuid()} title={act.tooltip}>
                          <IconButton
                            onClick={(evt) => act.onClick(evt, propss.data)}
                          >
                            <SvgIcon>
                              <path
                                d={
                                  act.icon().type.type.render().props.children
                                    .props.d
                                }
                              />
                            </SvgIcon>
                          </IconButton>
                        </Tooltip>
                      ))}
                    </>
                  )}
                </Fragment>
              );
            }
            return <MTableActions {...propss} />;
          }, []),
        }}
        pageSize={pageSize}
        options={{
          toolbar,
          searchFieldAlignment: 'right',
          showTitle,
          exportButton: { csv: true, json: true },
          exportCsv: () => {
            exportXLSX(columns, data, title);
          },
          toolbarButtonAlignment: 'right',
          actionsColumnIndex:
            actionsIndex < 0 ? columns.length + 1 : actionsIndex,
          minBodyHeight: minBodyHeight && window.innerHeight - minBodyHeight,
          maxBodyHeight: maxBodyHeightValue,
          pageSize,
          paging,
          search,
          selection,
          grouping: group,
          showTextRowsSelected: false,
          headerStyle: {
            zIndex: isFiltering ? 1 : 10,
          },
          rowStyle,
          cellStyle,
        }}
        localization={{
          pagination: {
            labelRowsSelect: t('general.tables.rows'),
            labelDisplayedRows: `{from}-{to} ${t(
              'general.tables.labelDisplayRows'
            )} {count}`,
            firstTooltip: t('general.tables.firstTooltip'),
            previousTooltip: t('general.tables.firstTooltip'),
            nextTooltip: t('general.tables.firstTooltip'),
            lastTooltip: t('general.tables.firstTooltip'),
          },

          header: {
            actions: t('general.tables.actions'),
          },
          toolbar: {
            exportTitle: t('general.tables.export_title'),
            exportAriaLabel: t('general.tables.export'),
            exportCSVName: t('general.tables.exportXLSXName'),
            exportPDFName: t('general.tables.exportPDFName'),
            searchPlaceholder: t('general.tables.search_placeholder'),
            nRowsSelected: `{0} ${t('general.tables.nRowsSelected')}`,
          },
          body: {
            emptyDataSourceMessage: t('general.tables.no-data-to-display'),
            exportTitle: t('general.tables.export_title'),
            exportAriaLabel: t('general.export'),
            exportCSVName: t('general.tables.exportXLSXName'),
            exportPDFName: t('general.exportPDFName'),
            searchPlaceholder: t('general.tables.search_placeholder'),
          },
          grouping: {
            placeholder: t('general.tables.groupingPlaceholder'),
            groupedBy: t('general.tables.groupedBy'),
          },
        }}
      />
    </MuiThemeProvider>
  );
}

materialTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape).isRequired,
  title: PropTypes.string,
  t: PropTypes.func.isRequired,
  pageSize: PropTypes.number,
  onRefreshClick: PropTypes.func,
  onAddClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onRemoveClick: PropTypes.func,
  onPrintClick: PropTypes.func,
  onSentClick: PropTypes.func,
  onApplyClick: PropTypes.func,
  filterToggle: PropTypes.func,
  onHistoryViewClick: PropTypes.func,
  filterBuildFunction: PropTypes.func,
  onApproveClick: PropTypes.func,
  onReproveClick: PropTypes.func,
  onSelectionChange: PropTypes.func,
  onJsonExport: PropTypes.func,
  onCardClick: PropTypes.func,
  onPrintReceiptClick: PropTypes.func,
  paging: PropTypes.bool,
  search: PropTypes.bool,
  showTitle: PropTypes.bool,
  selection: PropTypes.bool,
  isFiltering: PropTypes.bool,
  actionsIndex: PropTypes.number,
  minBodyHeight: PropTypes.number,
  maxBodyHeight: PropTypes.number,
  actionsPosition: PropTypes.string,
  onViewMetricsClick: PropTypes.func,
  onViewVisualizationMetricsClick: PropTypes.func,
  onActivityCorrectionClick: PropTypes.func,
  memoType: PropTypes.string,
  onViewTicketClick: PropTypes.func,
  onPrintOneReceiptClick: PropTypes.func,
  actionsDisabled: PropTypes.bool,
  actionsDisabledByConditional: PropTypes.func,
  isLoading: PropTypes.bool,
  rowStyle: PropTypes.oneOfType(
    PropTypes.objectOf(PropTypes.any),
    PropTypes.func
  ),
  cellStyle: PropTypes.oneOfType(
    PropTypes.shape(PropTypes.shape),
    PropTypes.func
  ),
  onCorrectionStudentActivityClick: PropTypes.func,
  group: PropTypes.bool,
  maxBodyHeightAuto: PropTypes.bool,
  onMedicationViewRecipe: PropTypes.func,
  onApplyMedicationClick: PropTypes.func,
  onCancelApplyMedicationClick: PropTypes.func,
  toolbar: PropTypes.bool,
  onAppClick: PropTypes.shape({
    tooltip: PropTypes.string.isRequired,
    callback: PropTypes.func.isRequired,
  }),
  onViewVisualizationClick: PropTypes.shape({
    tooltip: PropTypes.string.isRequired,
    callback: PropTypes.func.isRequired,
  }),
  customAction: PropTypes.shape({
    name: PropTypes.string.isRequired,
    icon: PropTypes.element.isRequired,
    tooltip: PropTypes.string.isRequired,
    callback: PropTypes.func.isRequired,
  }),
  customTableHeader: PropTypes.func,
  fixedFirstColumn: PropTypes.bool,
  customActions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      icon: PropTypes.func.isRequired,
      tooltip: PropTypes.string.isRequired,
      toolbarOnSelect: PropTypes.bool.isRequired,
      callback: PropTypes.func.isRequired,
    })
  ),
};

materialTable.defaultProps = {
  title: '',
  pageSize: 10,
  paging: true,
  search: true,
  showTitle: true,
  selection: false,
  onRemoveClick: null,
  onPrintClick: null,
  onSentClick: null,
  onApplyClick: null,
  onRefreshClick: null,
  onHistoryViewClick: null,
  onPrintReceiptClick: null,
  onCardClick: null,
  filterBuildFunction: null,
  onEditClick: null,
  onAddClick: null,
  filterToggle: null,
  isFiltering: false,
  onApproveClick: null,
  onReproveClick: null,
  onSelectionChange: null,
  onJsonExport: null,
  actionsIndex: -1,
  actionsPosition: 'row',
  minBodyHeight: 0,
  maxBodyHeight: 0,
  onViewMetricsClick: null,
  onViewVisualizationMetricsClick: null,
  onActivityCorrectionClick: null,
  onViewTicketClick: null,
  onPrintOneReceiptClick: null,
  memoType: '',
  actionsDisabled: false,
  actionsDisabledByConditional: null,
  isLoading: false,
  rowStyle: {},
  onCorrectionStudentActivityClick: null,
  group: false,
  maxBodyHeightAuto: false,
  onMedicationViewRecipe: null,
  onApplyMedicationClick: null,
  onCancelApplyMedicationClick: null,
  toolbar: true,
  onAppClick: null,
  onViewVisualizationClick: null,
  customAction: null,
  customTableHeader: null,
  cellStyle: {},
  fixedFirstColumn: false,
  customActions: [],
};

export default withTranslation('common')(materialTable);
